import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/admin/components/ui/tabs';
import {
  AdminChatAmbiguousIdentityError,
  type AdminChatIdentityCandidate,
  type AdminChatTraceResponse,
  type TraceRequest,
} from '../../api';
import { DetailPanel, type DetailSubTab } from '../detail/detail-panel';
import { PerformanceTab } from '../performance/performance-tab';
import { RequestSummary } from '../performance/request-summary';
import { RawTab } from '../raw/raw-tab';
import { CopyId } from './copy-id';
import { RequestList } from './request-list';
import { TimelinePanel } from './timeline-panel';
import { TraceHeader } from './trace-header';
import { formatDateTime } from '../../format';
import {
  buildTracePresentation,
  sortTraceRequests,
  type TracePresentation,
  type TracePresentationEvent,
  type TraceRequestActivitySummary,
} from '../../trace/presentation';

const TAB_VALUES = ['trace', 'performance', 'raw'] as const;
export type TraceExplorerTab = (typeof TAB_VALUES)[number];

export type TraceExplorerCandidateTarget = {
  tenant_id: string;
  agent_id: string;
  agent_group_id?: string;
  user_id?: string;
  session_id?: string;
};

export function TraceExplorer({
  identityKey,
  loadTrace,
  initialLoading = false,
  conversationLink,
  candidateTitle = 'Choose chat identity',
  buildCandidateTarget,
  activeTab,
  onTabChange,
  hideTabs = false,
}: {
  identityKey: string;
  loadTrace: (requestID?: string) => Promise<AdminChatTraceResponse>;
  initialLoading?: boolean;
  conversationLink?: boolean;
  candidateTitle?: string;
  buildCandidateTarget?: (
    candidate: AdminChatIdentityCandidate
  ) => TraceExplorerCandidateTarget;
  activeTab?: TraceExplorerTab;
  onTabChange?: (tab: TraceExplorerTab) => void;
  hideTabs?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIDParam = searchParams.get('request_id') || undefined;
  const urlTab = TAB_VALUES.includes(
    searchParams.get('tab') as TraceExplorerTab
  )
    ? (searchParams.get('tab') as TraceExplorerTab)
    : 'trace';
  const selectedTab = activeTab || urlTab;
  const [trace, setTrace] = React.useState<AdminChatTraceResponse | null>(null);
  const traceCache = React.useRef(new Map<string, AdminChatTraceResponse>());
  const inFlightTraceRequests = React.useRef(new Set<string>());
  const [requestCatalog, setRequestCatalog] = React.useState<
    AdminChatTraceResponse['requests']
  >([]);
  const [candidates, setCandidates] = React.useState<
    AdminChatIdentityCandidate[]
  >([]);
  const [selectedEventID, setSelectedEventID] = React.useState('');
  const [showInternal, setShowInternal] = React.useState(false);
  const [showLifecycleOnly, setShowLifecycleOnly] = React.useState(false);
  const [eventKinds, setEventKinds] = React.useState<Set<string>>(
    () =>
      new Set([
        'user_message',
        'assistant_message',
        'tool_call',
        'persist_event',
        'turn_marker',
      ])
  );
  const [warningsOnly, setWarningsOnly] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [detailTab, setDetailTab] = React.useState<DetailSubTab>('overview');
  const [loading, setLoading] = React.useState(initialLoading);
  const [error, setError] = React.useState<string | null>(null);
  const requestIDParamRef = React.useRef(requestIDParam);
  requestIDParamRef.current = requestIDParam;

  const loadTraceBundle = React.useCallback(
    async (requestID?: string, initial = false) => {
      const cacheKey = requestID || '__default__';
      if (inFlightTraceRequests.current.has(cacheKey)) return;
      inFlightTraceRequests.current.add(cacheKey);
      if (initial) setLoading(true);
      setError(null);
      setCandidates([]);
      try {
        let response = await loadTrace(requestID);
        const selectedID = preferredRequestID(response, requestID);
        if (
          !requestID &&
          selectedID &&
          selectedID !== response.selected_request_id
        ) {
          response = await loadTrace(selectedID);
        }
        setRequestCatalog(current =>
          sortTraceRequests(
            response.requests.length > 0 ? response.requests : current
          )
        );
        setTrace(current => {
          const catalog =
            response.requests.length > 0
              ? sortTraceRequests(response.requests)
              : current?.requests || [];
          const next = {
            ...response,
            selected_request_id: selectedID,
            requests: catalog,
          };
          if (selectedID) traceCache.current.set(selectedID, next);
          return next;
        });
        setSelectedEventID(current =>
          response.events.some(event => event.id === current)
            ? current
            : response.events[0]?.id || ''
        );
      } catch (err) {
        if (
          err instanceof AdminChatAmbiguousIdentityError ||
          (err instanceof Error && 'candidates' in err)
        ) {
          const ambiguous = err as AdminChatAmbiguousIdentityError;
          setCandidates(ambiguous.candidates || []);
          setTrace(null);
          setError(ambiguous.message);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load trace');
        }
      } finally {
        inFlightTraceRequests.current.delete(cacheKey);
        if (initial) setLoading(false);
      }
    },
    [loadTrace]
  );

  React.useEffect(() => {
    traceCache.current.clear();
    inFlightTraceRequests.current.clear();
    setRequestCatalog([]);
    loadTraceBundle(requestIDParamRef.current, true);
  }, [identityKey, loadTraceBundle]);

  React.useEffect(() => {
    if (!requestIDParam || requestIDParam === trace?.selected_request_id)
      return;
    const cached = traceCache.current.get(requestIDParam);
    if (cached) {
      setTrace(cached);
      setSelectedEventID(current =>
        cached.events.some(event => event.id === current)
          ? current
          : cached.events[0]?.id || ''
      );
      return;
    }
    loadTraceBundle(requestIDParam, false);
  }, [loadTraceBundle, requestIDParam, trace?.selected_request_id]);

  const presentation = React.useMemo(
    () => (trace ? buildTracePresentation(trace) : null),
    [trace]
  );
  const effectiveSelectedRequestID =
    requestIDParam || trace?.selected_request_id || '';
  const request = selectedRequest(trace, effectiveSelectedRequestID);
  const requestActivity = presentation?.requestActivity || new Map();
  const visibleRequests = sortTraceRequests(
    (requestCatalog.length > 0 ? requestCatalog : trace?.requests || []).filter(
      item => {
        if (!showInternal && item.internal) return false;
        if (
          showLifecycleOnly ||
          item.request_id === effectiveSelectedRequestID
        ) {
          return true;
        }
        return !isLifecycleOnlyRequest(
          item,
          trace,
          requestActivity.get(item.request_id)
        );
      }
    )
  );
  const filteredPresentation = React.useMemo(
    () =>
      filterPresentation(presentation, {
        eventKinds,
        warningsOnly,
        search,
      }),
    [eventKinds, presentation, search, warningsOnly]
  );
  const filteredPresentationEvents = React.useMemo(
    () => flattenPresentationEvents(filteredPresentation),
    [filteredPresentation]
  );
  const selectedPresentationEvent =
    filteredPresentationEvents.find(
      item => item.event.id === selectedEventID
    ) ||
    filteredPresentationEvents[0] ||
    null;
  const selectedEvent = selectedPresentationEvent?.event || null;

  React.useEffect(() => {
    const nextSelectedID = selectedEvent?.id || '';
    if (selectedEventID !== nextSelectedID) {
      setSelectedEventID(nextSelectedID);
    }
  }, [selectedEvent?.id, selectedEventID]);

  const selectCandidate = (candidate: AdminChatIdentityCandidate) => {
    const target = buildCandidateTarget?.(candidate) || candidate;
    const next = new URLSearchParams(searchParams);
    next.set('tenant_id', target.tenant_id);
    next.set('agent_id', target.agent_id);
    setOrDelete(next, 'agent_group_id', target.agent_group_id);
    setOrDelete(next, 'user_id', target.user_id);
    setOrDelete(next, 'session_id', target.session_id);
    next.delete('session_run_id');
    next.delete('request_id');
    setSearchParams(next);
  };

  const selectRequest = (requestID: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('request_id', requestID);
    setSearchParams(next, { replace: true });
  };

  const setTab = (tab: string) => {
    if (!TAB_VALUES.includes(tab as TraceExplorerTab)) return;
    if (onTabChange) {
      onTabChange(tab as TraceExplorerTab);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const toggleKind = (kind: string) => {
    setEventKinds(current => {
      const next = new Set(current);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  };

  const showTraceTab = (
    <div className="space-y-4">
      <RequestSummary request={request} trace={trace} />
      <div className="grid gap-4 xl:h-[calc(100dvh-22rem)] xl:min-h-[360px] xl:grid-cols-[280px_minmax(0,1fr)_360px]">
        <RequestList
          requests={visibleRequests}
          requestActivity={requestActivity}
          selectedRequestID={effectiveSelectedRequestID}
          showInternal={showInternal}
          showLifecycleOnly={showLifecycleOnly}
          onToggleInternal={setShowInternal}
          onToggleLifecycleOnly={setShowLifecycleOnly}
          onSelect={selectRequest}
        />
        <TimelinePanel
          presentation={filteredPresentation}
          unfilteredPresentation={presentation}
          selectedEventID={selectedEvent?.id || ''}
          eventKinds={eventKinds}
          warningsOnly={warningsOnly}
          search={search}
          loading={loading}
          onToggleKind={toggleKind}
          onWarningsOnly={setWarningsOnly}
          onSearch={setSearch}
          onSelect={item => {
            setSelectedEventID(item.event.id);
            setDetailTab('overview');
          }}
        />
        <DetailPanel
          item={selectedPresentationEvent}
          diagnostics={traceDiagnostics(trace)}
          detailTab={detailTab}
          onDetailTab={setDetailTab}
        />
      </div>
    </div>
  );
  const showPerformanceTab = (
    <PerformanceTab
      trace={trace}
      request={request}
      selectedRequestID={effectiveSelectedRequestID}
      onRequestSelect={selectRequest}
      presentation={presentation}
      onNavigate={eventID => {
        const target = presentation?.eventIndex.get(eventID);
        if (target && !eventKinds.has(target.event.kind)) {
          setEventKinds(current => new Set(current).add(target.event.kind));
        }
        setSelectedEventID(eventID);
        setTab('trace');
      }}
    />
  );
  const showRawTab = <RawTab trace={trace} />;
  const visibleTabContent =
    selectedTab === 'performance'
      ? showPerformanceTab
      : selectedTab === 'raw'
        ? showRawTab
        : showTraceTab;

  return (
    <>
      <TraceHeader
        trace={trace}
        loading={loading}
        onRefresh={() => loadTraceBundle()}
      />
      {conversationLink && trace?.header?.conversation_id ? (
        <div className="px-4 lg:px-6">
          <Button asChild variant="outline" size="sm">
            <Link
              to={`/conversations/${encodeURIComponent(trace.header.conversation_id)}?${traceConversationSearchParams(trace.header, effectiveSelectedRequestID)}`}
            >
              Open conversation
            </Link>
          </Button>
        </div>
      ) : null}

      <div className="px-4 lg:px-6">
        {error ? (
          <div className="mb-3 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        ) : null}
        {candidates.length > 0 ? (
          <CandidatePicker
            title={candidateTitle}
            candidates={candidates}
            onSelect={selectCandidate}
          />
        ) : hideTabs ? (
          <>
            {traceDiagnostics(trace).length > 0 ? (
              <TraceDiagnostics trace={trace} />
            ) : null}
            {visibleTabContent}
          </>
        ) : (
          <Tabs value={selectedTab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="trace">Trace</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="raw">Raw</TabsTrigger>
            </TabsList>
            {traceDiagnostics(trace).length > 0 ? (
              <TraceDiagnostics trace={trace} />
            ) : null}
            <TabsContent value="trace">{showTraceTab}</TabsContent>
            <TabsContent value="performance">{showPerformanceTab}</TabsContent>
            <TabsContent value="raw">{showRawTab}</TabsContent>
          </Tabs>
        )}
      </div>
    </>
  );
}

function selectedRequest(
  trace: AdminChatTraceResponse | null,
  selectedRequestID?: string
) {
  if (!trace) return null;
  return (
    trace.requests.find(request => request.request_id === selectedRequestID) ||
    trace.requests.find(
      request => request.request_id === trace.selected_request_id
    ) ||
    trace.requests.find(request => !request.internal) ||
    trace.requests[0] ||
    null
  );
}

function traceDiagnostics(trace: AdminChatTraceResponse | null) {
  return Array.isArray(trace?.diagnostics) ? trace.diagnostics : [];
}

function TraceDiagnostics({ trace }: { trace: AdminChatTraceResponse | null }) {
  return (
    <div className="my-3 space-y-2">
      {traceDiagnostics(trace).map(diagnostic => (
        <div
          key={diagnostic.id}
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-200"
        >
          <div className="font-medium">{diagnostic.title}</div>
          {diagnostic.detail ? (
            <div className="mt-1 text-xs opacity-85">{diagnostic.detail}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function traceConversationSearchParams(
  header: AdminChatTraceResponse['header'],
  requestID?: string
) {
  const params = new URLSearchParams();
  params.set('tenant_id', header.tenant_id);
  params.set('agent_id', header.agent_id);
  if (header.agent_group_id)
    params.set('agent_group_id', header.agent_group_id);
  if (header.user_id) params.set('user_id', header.user_id);
  if (header.session_id) params.set('session_id', header.session_id);
  if (requestID) {
    params.set('request_id', requestID);
    params.set('tab', 'trace');
  }
  return params.toString();
}

function preferredRequestID(
  trace: AdminChatTraceResponse,
  requestedID?: string
) {
  if (requestedID) return requestedID;
  const orderedRequests = sortTraceRequests(trace.requests);
  const selected = trace.requests.find(
    request => request.request_id === trace.selected_request_id
  );
  if (selected && requestHasPrimaryActivity(selected, trace)) {
    return selected.request_id;
  }
  return (
    orderedRequests.find(
      request => !request.internal && requestHasPrimaryActivity(request, trace)
    )?.request_id ||
    orderedRequests.find(
      request =>
        !request.internal && request.request_id !== trace.selected_request_id
    )?.request_id ||
    orderedRequests.find(request => !request.internal)?.request_id ||
    orderedRequests[0]?.request_id ||
    trace.selected_request_id ||
    ''
  );
}

function requestHasPrimaryActivity(
  request: TraceRequest,
  trace: AdminChatTraceResponse
) {
  if ((request.token_total || 0) > 0) return true;
  if ((request.tool_cost?.calls || 0) > 0) return true;
  return trace.events.some(
    event =>
      event.request_id === request.request_id &&
      (event.kind === 'user_message' ||
        event.kind === 'assistant_message' ||
        event.kind === 'tool_call' ||
        event.kind === 'llm_call' ||
        event.kind === 'persist_event')
  );
}

function isLifecycleOnlyRequest(
  request: TraceRequest,
  trace: AdminChatTraceResponse | null,
  activity?: TraceRequestActivitySummary
) {
  if (activity) return activity.lifecycleOnly;
  if (!trace) return false;
  return !requestHasPrimaryActivity(request, trace);
}

function filterPresentation(
  current: TracePresentation | null,
  options: {
    eventKinds: Set<string>;
    warningsOnly: boolean;
    search: string;
  }
): TracePresentation | null {
  if (!current) return null;
  const normalizedSearch = options.search.trim().toLowerCase();
  return {
    ...current,
    requestGroups: current.requestGroups.flatMap(group => {
      const turns = group.turns.flatMap(turn => {
        const events = turn.events.filter(item =>
          presentationEventMatches(item, options)
        );
        const collapsedEvents = turn.collapsedEvents.filter(item => {
          if (!normalizedSearch) return false;
          return presentationEventMatches(item, options);
        });
        if (events.length === 0 && collapsedEvents.length === 0) return [];
        return [{ ...turn, events, collapsedEvents }];
      });
      if (turns.length === 0) return [];
      return [{ ...group, turns }];
    }),
  };
}

function presentationEventMatches(
  item: TracePresentationEvent,
  options: {
    eventKinds: Set<string>;
    warningsOnly: boolean;
    search: string;
  }
) {
  if (!options.eventKinds.has(item.event.kind)) return false;
  if (options.warningsOnly && !item.warning) return false;
  const normalizedSearch = options.search.trim().toLowerCase();
  if (normalizedSearch && !item.text.toLowerCase().includes(normalizedSearch)) {
    return false;
  }
  return true;
}

function flattenPresentationEvents(current: TracePresentation | null) {
  if (!current) return [];
  return current.requestGroups.flatMap(group =>
    group.turns.flatMap(turn => [...turn.events, ...turn.collapsedEvents])
  );
}

function setOrDelete(params: URLSearchParams, key: string, value?: string) {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}

function CandidatePicker({
  title,
  candidates,
  onSelect,
}: {
  title: string;
  candidates: AdminChatIdentityCandidate[];
  onSelect: (candidate: AdminChatIdentityCandidate) => void;
}) {
  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Session</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Conversation</TableHead>
              <TableHead>Last activity</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {candidates.map(candidate => (
              <TableRow
                key={`${candidate.tenant_id}:${candidate.agent_id}:${candidate.session_id}`}
              >
                <TableCell>{candidate.title || '-'}</TableCell>
                <TableCell>
                  <CopyId value={candidate.tenant_id} label="tenant id" />
                </TableCell>
                <TableCell>
                  <CopyId value={candidate.agent_id} label="agent id" />
                </TableCell>
                <TableCell>
                  <CopyId
                    value={candidate.agent_group_id}
                    label="agent group id"
                  />
                </TableCell>
                <TableCell>
                  <CopyId value={candidate.session_id} label="session id" />
                </TableCell>
                <TableCell>
                  <CopyId value={candidate.user_id} label="user id" />
                </TableCell>
                <TableCell>
                  <CopyId
                    value={candidate.conversation_id}
                    label="conversation id"
                  />
                </TableCell>
                <TableCell>
                  {formatDateTime(candidate.last_activity_at)}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" onClick={() => onSelect(candidate)}>
                    Select
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
